import { DATASET } from '@utils/constants';
import { get } from 'lodash';
import getMetricsData from '@api/metricContainer';
import instance from '@api/axiosInstance';
import { getInvertedPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import { formatTimeseriesData } from '@src/components/metrics-panel-hz/utils/format';
import { appendFlowIdentifier } from '@utils/filter';
import { useSelector } from 'react-redux';
import {
  getFirstAppliedFilter,
  intervalSelector,
  realTimeModeOnTrendsSelector,
  supportedMetricsSelector,
} from '@store/common/selectors';
import { useQuery } from '@tanstack/react-query';
import { getFlowMetrics } from '@utils/flow/utils';

function useQueryParams() {
  const filterFull = useSelector(getFirstAppliedFilter);
  const { rule: filter, _rKey = 0 } = filterFull;
  const allowedMetrics: any = useSelector(supportedMetricsSelector);
  const interval = useSelector(intervalSelector);
  return {
    interval,
    filter,
    _rKey,
    allowedMetrics,
  };
}

export const createQuery = (
  metricList: string[] = [],
  isLiveMode: any = false,
  interval,
  granularity = 'ALL',
  filter = [] as any[][],
  options = {},
) => {
  const query = {
    type: 'timeseries',
    dataset: DATASET,
    granularity: granularity,
    options: {
      withTotals: true,
      ...(options || {}),
    },
    interval,
    filter,
    isLiveMode,
    metrics: metricList,
    context: window.conviva.context,
  };

  return {
    queries: [query],
  };
};

function useAppFlowFetch({ flowMeta }: { flowId: string; flowMeta?: any }) {
  const isLiveMode = useSelector(realTimeModeOnTrendsSelector);
  const { identifier } = flowMeta || {};
  const metrics = getFlowMetrics(flowMeta);
  const { interval, filter, _rKey, allowedMetrics } = useQueryParams();

  const isDeleted = flowMeta?.isDeleted;

  const filteredMetrics = metrics.filter(
    (metric) =>
      (metric && allowedMetrics && metric in allowedMetrics) ||
      (metric &&
        allowedMetrics &&
        getInvertedPercentageMetricByKey({ metricKey: metric }) in allowedMetrics),
  );

  const allowedMetricList = [...new Set([...filteredMetrics])];
  const options = {};
  const payload = createQuery(
    allowedMetricList,
    isLiveMode,
    `${interval.from}/${interval.to}`,
    interval.granularity,
    appendFlowIdentifier(filter, identifier) as any,
    options,
  );

  const result = useQuery(
    ['overview', 'flow', payload.queries, _rKey],
    () =>
      getMetricsData(payload, {}, instance).then((response) => {
        const { data, totals, annotations } = get(response, ['data', '0'], {
          totals: {},
          data: [],
          annotations: [],
        });
        const formattedData = formatTimeseriesData({
          metrics: allowedMetricList,
          data,
          interval,
          annotations,
        });
        return { data: formattedData, totals };
      }),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !!metrics.length && !isDeleted, // Don't make API calls for deleted flows
      keepPreviousData: !!isLiveMode,
      staleTime: 5 * 60 * 1000, // 5m
    },
  );

  if (isDeleted) {
    return {
      data: null,
      isLoading: false,
      isFetching: false,
      isError: false,
      error: null,
      refetch: () => Promise.resolve(),
    };
  }

  return result;
}

export default useAppFlowFetch;
